import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import { sortBy } from 'lodash-es';
import { Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { X_SKIP_ALERT } from 'src/app/core/constants/http-headers';
import { USER_DASHBOARDS_URL_ROOT } from 'src/app/core/constants/route';
import { ROLE_EXTERNAL_AGENCY, ROLE_HOSPITAL_USER } from 'src/app/core/constants/user-management';
import { EntitledProfile, RoleRights } from 'src/app/core/models/entitlements/entitled-profile';
import { Entity } from 'src/app/core/models/entitlements/entity';
import { Role } from 'src/app/core/models/entitlements/role';
import { ForgotPasswordForm } from 'src/app/core/models/forgot-password-form';
import { JSONResponse } from 'src/app/core/models/json-response';
import { LoginForm } from 'src/app/core/models/login-form';
import { LoginResponse } from 'src/app/core/models/login-response';
import { Organization } from 'src/app/core/models/organization';
import { UpdatePasswordForm } from 'src/app/core/models/update-password-form';
import { User } from 'src/app/core/models/user';
import { EnvironmentService } from 'src/app/core/services/environment.service';
import { GlobalModalService } from 'src/app/core/services/global-modal.service';
import { PatientService } from 'src/app/core/services/patient.service';
import { getFullFormTimezone } from 'src/app/core/utils/date-util';
import { isString } from 'src/app/core/utils/type-util';
import {
  ORG_SUBTYPE_AETNA,
  ORG_SUBTYPE_COUNTYCARE,
  ORG_SUBTYPE_IL,
  ORG_TYPE_ACO_REACH,
  ORG_TYPE_MHN_ACO,
  ORG_TYPE_MSSP
} from '../constants/organization';
import { EntityNameCacheService } from './entity-name-cache.service';
import { SystemNotificationService } from './system-notification.service';
import { UserService } from './user.service';

const loginUrl = '/login/login.html';
const logoutUrl = '/logout/loggedout.html';
const forgotPasswordUrl = '/forgot/forgotpasswd.html';
const validateUserPasswordCheckUrl = '/users/validateUserPassword.html';
const updatePasswordUrl = '/forgot/resetpasswd.html';
const isOrganizationChangedUrl = '/dashboards/isOrganizationChanged.html';
const switchUserOrganizationUrl = '/switchlogin/switchUserOrganization.html';
const agreeToTermsOfUseUrl = '/users/updateprivacypolicy.html';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  loginRedirect: string;
  /**
   * Time in milliseconds between interactions in which the user's session is considered expired
   */
  inactivityTimeInterval: number;
  organization: Organization;
  linkedOrganizations: Organization[];
  allAvailableOrganizations: Organization[];
  orgId: number;
  orgLevel: number;
  user: User;
  entitledProfile: EntitledProfile;
  entity: Entity;
  role: Role;
  roleRights: RoleRights;
  timezone: string;
  fullFormTimezone: string;
  roleDashboardUrl: string;
  mappingKeys: { [key: string]: string };
  isUserRoleExternalAgency: boolean;
  isUserRoleHospitalUser: boolean;
  isMedicaidOrg: boolean;
  isMedicareOrg: boolean;
  isMHNACO: boolean;
  isMHNACOCountyCare: boolean;
  isMHNACOAetna: boolean;
  isACOREACH: boolean;
  isACOREACHIL: boolean;
  isMSSP: boolean;

  private _loginResponse: LoginResponse = null;
  private lastInterruptTime: number;

  private entitlements: Map<string, boolean> = new Map();

  private _loggedOut: Subject<void> = new Subject();
  loggedOut: Observable<void> = this._loggedOut.asObservable();

  private oktaAuthenticated = false;

  constructor(
    private http: HttpClient,
    private globalModalService: GlobalModalService,
    private idle: Idle,
    private userService: UserService,
    private patientService: PatientService,
    private systemNotificationService: SystemNotificationService,
    private entityNameCacheService: EntityNameCacheService,
    private modalService: NgbModal,
    private ngZone: NgZone,
    private router: Router,
    private environmentService: EnvironmentService,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private oktaAuthStateService: OktaAuthStateService
  ) {
    this.refreshLoginResponse();

    this.initializeIdleTimer();

    if (this.isAuthenticated()) {
      this.restartIdleTimer();
    }

    this.oktaAuthStateService.authState$.subscribe(async (state) => {
      if (this.oktaAuthenticated && !state.isAuthenticated) {
        await this.logout();
        this.router.navigate(['/login']);
      }
      this.oktaAuthenticated = state.isAuthenticated;
    });

    this.userService.authenticatedUserUpdate.subscribe((user) => {
      this.updateAuthenticatedUser(user);
    });

    this.systemNotificationService.notificationCountUpdated.subscribe((count) => {
      this.refreshLastNotificationReadDate(count);
    });
  }

  isAuthenticated(): boolean {
    return Boolean(this.loginResponse);
  }

  isAuthenticatedAsync(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.loginResponse) {
        resolve(true);
      } else {
        this.http
          .post<LoginResponse>(loginUrl, {})
          .pipe(
            tap((response: LoginResponse) => {
              this.loginResponse = response || null;
              if (response) {
                this.restartIdleTimer();
              }
              this.entityNameCacheService.clearCache();
              this.userService.clearCache();
              this.patientService.clearCache();
            })
          )
          .toPromise()
          .then(
            () => resolve(true),
            () => resolve(false)
          );
      }
    });
  }

  isOktaAuthenticated(): boolean {
    return this.oktaAuthenticated;
  }

  isAuthenticatedAndAgreed(): boolean {
    return this.isAuthenticated() && this.user && this.user.agree;
  }

  authenticate(loginForm: LoginForm): Observable<LoginResponse> {
    const userData = window.btoa(`${loginForm.username}:${loginForm.password}`);

    return this.http
      .post<LoginResponse>(
        loginUrl,
        {},
        {
          headers: new HttpHeaders({
            Authorization: `Basic ${userData}`
          })
        }
      )
      .pipe(
        tap((response: LoginResponse) => {
          this.loginResponse = response || null;
          if (response) {
            this.restartIdleTimer();
          }
          this.entityNameCacheService.clearCache();
          this.userService.clearCache();
          this.patientService.clearCache();
        })
      );
  }

  switchUserOrganization(orglevel: number): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(switchUserOrganizationUrl, '', {
        params: {
          orglevel: orglevel
        }
      })
      .pipe(
        tap((response: LoginResponse) => {
          this.loginResponse = response || null;
        })
      );
  }

  isEntitled(url: string): boolean {
    return this.entitlements.get(url) === true;
  }

  isEntitledGenericDashboard(tableCode: string): boolean {
    return this.isEntitled(`/dashboard/${tableCode}/rows`);
  }

  agreeToTermsOfUse(): Observable<boolean> {
    return this.http.post<boolean>(agreeToTermsOfUseUrl, '', {
      params: {
        userid: this.user.userId
      }
    });
  }

  forgotPassword(forgotPassword: ForgotPasswordForm): Observable<JSONResponse<string>> {
    return this.http.post<JSONResponse<string>>(forgotPasswordUrl, forgotPassword);
  }

  async logout(memberId = ''): Promise<void> {
    await this.http
      .get<void>(logoutUrl, {
        headers: {
          [X_SKIP_ALERT]: '1'
        },
        params: {
          memberId: memberId
        }
      })
      .toPromise();

    if (this.oktaAuthenticated) {
      this.oktaAuth.tokenManager.clear();
      await this.oktaAuth.signOut();
    }

    this.loginResponse = null;
  }

  get loginResponse(): LoginResponse {
    return this._loginResponse;
  }

  set loginResponse(loginResponse: LoginResponse) {
    this.refreshCurrentUserDetails(loginResponse);
  }

  get lastNotificationReadDate(): Date {
    return this.loginResponse?.lastNotificationReadDate;
  }

  updateAuthenticatedUser(user: User) {
    if (user.userId === this.user.userId) {
      this.user.firstName = user.firstName;
      this.user.lastName = user.lastName;
      this.user.nickName = user.nickName;
      this.user.passPhrase = user.passPhrase;
      this.user.challengeQuestion = user.challengeQuestion;
      this.user.challengeAnswer = user.challengeAnswer;
      this.user.timezone = user.timezone;
      this.user.emailId = user.emailId;
      this.user.telePin = user.telePin;
      this.user.phoneNo = user.phoneNo;
      this.user.mobileNo = user.mobileNo;
      this.user.agree = user.agree;
      this.user.asmStatus = user.asmStatus;

      this.refreshCurrentUserDetails(this.loginResponse);
    }
  }

  refreshLoginResponse() {
    this.loginResponse = JSON.parse(localStorage.getItem(this.environmentService.currentUserLocalStorageKey));
  }

  validateUserPassword(passwd: string): Observable<boolean> {
    return this.http.post<JSONResponse<boolean>>(validateUserPasswordCheckUrl, passwd).pipe(
      map((response) => {
        return false === response.result;
      })
    );
  }

  updatePassword(updatePassword: UpdatePasswordForm): Observable<JSONResponse<string>> {
    return this.http.post<JSONResponse<string>>(updatePasswordUrl, updatePassword);
  }

  isOrganizationChanged(orgLevel: number): Observable<boolean> {
    return this.http
      .get<JSONResponse<string>>(isOrganizationChangedUrl, {
        params: {
          orgLevel: orgLevel
        }
      })
      .pipe(
        map((response) => {
          if (isString(response.result)) {
            return response.result === 'true';
          } else {
            return null;
          }
        })
      );
  }

  expireSession() {
    this.loginRedirect = this.router.url;

    this.modalService.dismissAll();

    this.globalModalService.alert({
      bodyText: 'Your session has expired'
    });

    // This code may be called outside of Angular's zone so we need
    // to force it to run inside for the navigate call to work.
    this.ngZone.run(() => {
      this.router.navigate(['/logout']);
    });
  }

  initializeIdleTimer() {
    const timeoutWarningDuration = 60;

    this.idle.setIdleName(this.environmentService.idleName);
    this.idle.setTimeout(timeoutWarningDuration);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onTimeout.subscribe(() => {
      this.expireSession();
    });

    this.idle.onTimeoutWarning.subscribe((secondsRemaining) => {
      const message = `Due to inactivity, your session will expire in ${secondsRemaining} second${
        secondsRemaining > 1 ? 's' : ''
      }.`;

      if (secondsRemaining === timeoutWarningDuration) {
        this.globalModalService.alert(
          {
            headerText: 'Session Expiration Alert',
            bodyText: message,
            confirmButtonText: 'Stay Logged In',
            showCloseButton: false
          },
          {
            backdrop: 'static',
            keyboard: false
          }
        );
      } else {
        this.globalModalService.options.bodyText = message;
      }
    });

    this.idle.onInterrupt.subscribe(() => {
      const currentTime = Date.now();
      const secondsIdle = (currentTime - this.lastInterruptTime) / 1000;

      // We need to calculate the time between interrupts in case the
      // user is still logged in after their computer comes out of sleep mode.
      if (secondsIdle >= this.inactivityTimeInterval / 1000 + timeoutWarningDuration) {
        this.expireSession();
      }

      this.lastInterruptTime = currentTime;
    });
  }

  restartIdleTimer() {
    this.stopIdleTimer();
    this.lastInterruptTime = Date.now();
    this.idle.watch();
  }

  stopIdleTimer() {
    this.idle.stop();
  }

  private populateEntitlements() {
    this.entitlements.clear();

    this.entitlements.set(null, true);
    this.entitlements.set(undefined, true);
    this.entitlements.set('', true);

    if (this.roleRights && this.role) {
      const functionRights = this.roleRights[this.role.roleId];

      Object.keys(functionRights).forEach((url) => {
        const isEntitled = functionRights[url].authLevel > 1;

        this.entitlements.set(url, isEntitled);

        if (url.startsWith('/reports/') && isEntitled) {
          this.entitlements.set('/reports/*', true);
        }
      });
    }
  }

  private initializeSessionVariables() {
    this.linkedOrganizations = sortBy(this.loginResponse?.linkedOrganizations, [(org) => org.orgName.toLowerCase()]);
    this.entity = this.loginResponse?.entity;

    this.user = this.loginResponse?.userCredentials?.user;
    this.orgId = this.user?.orgId;
    this.orgLevel = this.user?.orgLevel;
    this.timezone = this.user?.timezone;
    this.fullFormTimezone = getFullFormTimezone(this.timezone);

    this.organization = this.user?.organization;
    this.inactivityTimeInterval = this.organization?.inactivityTimeInterval || 7200000;

    this.entitledProfile = this.loginResponse?.entitledProfile;
    this.role = this.entitledProfile?.role;
    this.roleRights = this.entitledProfile?.rolerights;
    this.roleDashboardUrl = this.role && this.getRoleDashboardUrl();
    this.mappingKeys = this.loginResponse?.mappingKeys;

    this.isUserRoleExternalAgency = this.role?.roleDescription === ROLE_EXTERNAL_AGENCY;
    this.isUserRoleHospitalUser = this.role?.roleDescription === ROLE_HOSPITAL_USER;

    this.isMHNACO = this.organization?.orgType === ORG_TYPE_MHN_ACO;
    this.isMHNACOCountyCare = this.isMHNACO && this.organization?.orgSubtype === ORG_SUBTYPE_COUNTYCARE;
    this.isMHNACOAetna = this.isMHNACO && this.organization?.orgSubtype === ORG_SUBTYPE_AETNA;
    this.isACOREACH = this.organization?.orgType === ORG_TYPE_ACO_REACH;
    this.isACOREACHIL = this.isACOREACH && this.organization?.orgSubtype === ORG_SUBTYPE_IL;
    this.isMSSP = this.organization?.orgType === ORG_TYPE_MSSP;

    this.isMedicaidOrg = this.isMHNACO;
    this.isMedicareOrg = this.isACOREACH || this.isMSSP;

    if (this.organization && this.linkedOrganizations) {
      this.allAvailableOrganizations = sortBy(
        [this.organization, ...this.linkedOrganizations],
        [(org) => org.orgName.toLowerCase()]
      );
    }

    // Idle timer is measured in seconds
    this.idle.setIdle(this.inactivityTimeInterval / 1000);

    if (this.isAuthenticated()) {
      // Need to restart the timer for the new idle duration to get applied
      this.restartIdleTimer();
    }
  }

  private getRoleDashboardUrl(): string {
    const defaultUrl = `/private/${USER_DASHBOARDS_URL_ROOT}`;

    return this.role.roleDashboard || defaultUrl;
  }

  private refreshCurrentUserDetails(loginResponse: LoginResponse) {
    localStorage.setItem(this.environmentService.currentUserLocalStorageKey, JSON.stringify(loginResponse));
    this._loginResponse = loginResponse;

    this.initializeSessionVariables();
    this.populateEntitlements();

    if (loginResponse == null) {
      this._loggedOut.next();
    }
  }

  private refreshLastNotificationReadDate(count: number) {
    if (count === 0 && this.loginResponse) {
      this.loginResponse.lastNotificationReadDate = new Date().toISOString() as unknown as Date;
      this.refreshCurrentUserDetails(this.loginResponse);
    }
  }
}
